import {AccordionSummary, Stack, Typography, useTheme} from '@mui/material';
import {RefundTaggingEnum} from 'main/analytics/gaTagsEnum';
import {CTAButtonFixed, MainFooter, Row, StyledGrid} from 'presentation/components';
import {Loading} from 'presentation/components/Loading/Loading';
import Icon from 'presentation/icons';
import {useMediaQueries} from 'presentation/pages/private/hooks';
import {UserInfo} from 'presentation/pages/private/refund/components';
import {
	StyledAccordion,
	StyledAccordionDetails,
	StyledBox,
	StyledStack,
} from 'presentation/pages/private/refund/components/RequestInfo/styles';
import {useRefundRequest} from 'presentation/pages/private/refund/hooks';
import {translations} from 'presentation/translations-new';
import {formatPrice, formatText} from 'presentation/utils';
import React, {Fragment} from 'react';
import {CustomContainer, StyledFooterContainer} from '../Conference/styles';
import type {ConferenceProps} from '../Conference/types';
import {calculateAndFormatTotalSessionsValue} from '../Therapies/components/AddTherapy/components/TherapySessions/utils';
import {useTherapies} from '../Therapies/hooks/useTherapies';
import {BankInfoCardTherapies} from './components/BankInfoCardTherapies';
import {ContactInfoCardTherapies} from './components/ContactInfoCardTherapies';
import {FiscalDocumentCardTherapies} from './components/FiscalDocumentCardTherapies/index';
import {ProviderAndPatient} from './components/ProviderAndPatient';

const {
	finishRequest,
	refundInfo: {refundRequests, expectedRefund, value: valueLabel, serviceDate: serviceDateLabel},
} = translations['pt-br'].privateFlow.refund.conferencePage;
const {sessions: sessionsLabel, totalReceiptValue} =
	translations['pt-br'].privateFlow.refund.therapies;

export const ConferenceTherapies = ({handleNextStep, isLoading}: ConferenceProps) => {
	const {isMobile, isLaptop} = useMediaQueries();
	const theme = useTheme();
	const {therapies} = useTherapies();
	const {requests, listRequestForms} = useRefundRequest();
	const firstRequestData = requests.requestData?.[0];

	const fiscalDocumentProps = {
		number: firstRequestData?.financialData?.invoiceNumber.toString() || '-',
		value: firstRequestData?.financialData?.invoiceAmount || '-',
		verifierCode: firstRequestData?.financialData?.invoiceDigit,
		state: firstRequestData?.financialData?.invoiceIssueState,
		city: formatText(firstRequestData?.financialData?.invoiceIssueCity),
		uploads: listRequestForms?.[0]?.uploads?.map(file => file.filename) || [],
	};

	const totalValue: number =
		requests.requestData?.reduce(
			(acc, item) => acc + parseFloat(item.financialData.invoiceAmount.replace(',', '.')),
			0,
		) || 0;

	return (
		<Fragment>
			<CustomContainer>
				<StyledGrid.Root spacing={3}>
					<StyledGrid.Item md={12} lg={8}>
						<Stack spacing={2}>
							{isLaptop ? <UserInfo /> : null}

							<ContactInfoCardTherapies />
							<BankInfoCardTherapies />

							<Typography variant="subtitle2" color={'text.secondary'}>
								{refundRequests}
							</Typography>

							<ProviderAndPatient />

							{requests.requestData && therapies.length > 0
								? therapies.map((therapy, index) => (
										<StyledAccordion
											sx={{borderRadius: 1, overflow: 'hidden'}}
											key={index}
											defaultExpanded={therapies.length === 1}
											disableGutters>
											<AccordionSummary
												expandIcon={
													<Icon
														name="expand-more-filled"
														color={theme.palette.primary.main}
														size={32}
													/>
												}
												aria-controls="panel1-content"
												id="panel1-header">
												<Stack sx={{gap: 0.5}}>
													<Typography>
														{formatText(
															therapy.therapyType?.description,
														)}
													</Typography>
													<Typography>
														{expectedRefund}:{' '}
														{calculateAndFormatTotalSessionsValue(
															therapy?.sessions || [],
														)}
													</Typography>
												</Stack>
											</AccordionSummary>
											<StyledAccordionDetails>
												<StyledBox>
													<StyledStack spacing={2}>
														<Typography
															variant="subtitle2"
															color={'text.secondary'}>
															{sessionsLabel}
														</Typography>
														{therapy.sessions?.map(
															(session, sIndex) => (
																<Row.Compound
																	hiddenDivider={
																		sIndex ===
																		therapy.sessions.length - 1
																	}
																	key={sIndex}
																	items={[
																		{
																			title: serviceDateLabel,
																			value:
																				session?.serviceDate ||
																				'-',
																		},
																		{
																			title: valueLabel,
																			value: session?.sessionValue
																				? formatPrice(
																						session.sessionValue,
																					)
																				: '-',
																		},
																	]}
																/>
															),
														)}
													</StyledStack>
												</StyledBox>
											</StyledAccordionDetails>
										</StyledAccordion>
									))
								: null}

							<FiscalDocumentCardTherapies {...fiscalDocumentProps} />

							<CTAButtonFixed.Root justifyContent="space-between">
								<Stack
									direction="row"
									flex={1}
									gap={1}
									alignItems="center"
									justifyContent={isMobile ? 'space-between' : 'flex-end'}>
									{requests.requestData ? (
										<Fragment>
											<Typography variant="subtitle1" color="text.primary">
												{totalReceiptValue}
											</Typography>
											<Typography variant="h5" color="text.primary">
												{formatPrice(totalValue)}
											</Typography>
										</Fragment>
									) : null}
								</Stack>

								<Stack
									flex={0}
									gap={1}
									justifyContent="flex-end"
									flexDirection={isMobile ? 'column' : 'row'}>
									<CTAButtonFixed.Action
										data-tag-id={
											RefundTaggingEnum.CLICK_FINALIZAR_SOLICITACAO_REEMBOLSO
										}
										disabled={isLoading}
										size="large"
										onClick={handleNextStep}
										variant="contained"
										fullWidth={isMobile}
										endIcon={
											isLoading ? <Loading size={20} color="inherit" /> : null
										}>
										{finishRequest}
									</CTAButtonFixed.Action>
								</Stack>
							</CTAButtonFixed.Root>
						</Stack>
					</StyledGrid.Item>
					{isLaptop ? null : (
						<StyledGrid.Item md={12} lg={4}>
							<Stack spacing={2}>
								<UserInfo hideAccordion />
							</Stack>
						</StyledGrid.Item>
					)}
				</StyledGrid.Root>
			</CustomContainer>
			<StyledFooterContainer>
				<MainFooter />
			</StyledFooterContainer>
		</Fragment>
	);
};
